import { makeAutoObservable } from "mobx";
import {
  addDecisionsEvent,
  DbotEventDispatcher,
  DecisionContext,
} from "@/dbot/common/commonDomain";
import { DecisionType } from "@/common/decisions";
import { OptionFieldModel, StringFieldModel } from "@/common/commonForms";
import { cityItems, lookupNorwegianCity, ZipItem, zipItems } from "@/utils/postalCodes";

export type PreviousAddress = {
  addressLine1?: string;
  addressLine2?: string;
  zip?: string;
  city?: string;
  country?: string;
};

export type ChangeOfAddress = {
  type: DecisionType.changeOfAddress;
  options?: ChangeOfAddressOption[];
  addressLine1?: string;
  addressLine2?: string;
  zip?: string;
  city?: string;
  country?: string;
  mailingAddressLine1?: string;
  mailingAddressLine2?: string;
  mailingZip?: string;
  mailingCity?: string;
  mailingCountry?: string;
  previousAddress?: PreviousAddress;
  previousMailingAddress?: PreviousAddress;
};

export enum ChangeOfAddressOption {
  businessAddress = "BUSINESS_ADDRESS",
  mailingAddress = "MAILING_ADDRESS",
  removeMailingAddress = "REMOVE_MAILING_ADDRESS",
}

export const changeOfAddressOptions = [
  ChangeOfAddressOption.businessAddress,
  ChangeOfAddressOption.mailingAddress,
  ChangeOfAddressOption.removeMailingAddress,
];

const norwayCountryCode = "NO";

function defaultMailingCountry(
  options: OptionFieldModel<ChangeOfAddressOption>,
  country?: string,
): string {
  if (country?.trim()) return country;
  return options.selectedIds.has(ChangeOfAddressOption.mailingAddress) ? norwayCountryCode : "";
}

function changeOfAddressOptionLabel(option: ChangeOfAddressOption) {
  switch (option) {
    case ChangeOfAddressOption.businessAddress:
      return { key: "decisions.changeOfAddress.options.business-address" } as const;
    case ChangeOfAddressOption.mailingAddress:
      return { key: "decisions.changeOfAddress.options.mailing-address" } as const;
    case ChangeOfAddressOption.removeMailingAddress:
      return { key: "decisions.changeOfAddress.options.remove-mailing-address" } as const;
  }
}

export class ChangeOfAddressForm {
  readonly type = DecisionType.changeOfAddress;

  changeOptions: OptionFieldModel<ChangeOfAddressOption>;
  addressLine1: StringFieldModel;
  addressLine2: StringFieldModel;
  zip: StringFieldModel;
  city: StringFieldModel;
  country: StringFieldModel;
  mailingAddressLine1: StringFieldModel;
  mailingAddressLine2: StringFieldModel;
  mailingZip: StringFieldModel;
  mailingCity: StringFieldModel;
  mailingCountry: StringFieldModel;
  readonly previousAddress?: PreviousAddress;
  readonly previousMailingAddress?: PreviousAddress;
  readonly dispatch?: DbotEventDispatcher;

  constructor(
    readonly context: DecisionContext<ChangeOfAddress>,
    data?: ChangeOfAddress,
    dispatch?: DbotEventDispatcher,
  ) {
    this.dispatch = dispatch;

    makeAutoObservable(
      this,
      { type: false, dispatch: false, previousAddress: false, previousMailingAddress: false },
      { autoBind: true },
    );

    const company = context.initialState?.company;
    this.changeOptions = new OptionFieldModel<ChangeOfAddressOption>({
      options: changeOfAddressOptions,
      selectedIds: data?.options ?? [ChangeOfAddressOption.businessAddress],
      idFn: (option) => option,
      labelFn: changeOfAddressOptionLabel,
      onChange: this.handleChangeOptions,
      required: true,
    });
    this.previousAddress =
      data?.previousAddress ??
      (company
        ? {
            addressLine1: company.businessAddress1,
            addressLine2: company.businessAddress2,
            zip: company.businessZip,
            city: company.businessCity,
            country: company.businessCountry,
          }
        : undefined);
    this.previousMailingAddress =
      data?.previousMailingAddress ??
      (company
        ? {
            addressLine1: company.mailingAddress1,
            addressLine2: company.mailingAddress2,
            zip: company.mailingZip,
            city: company.mailingCity,
            country: company.mailingCountry,
          }
        : undefined);

    this.addressLine1 = new StringFieldModel({
      value: data?.addressLine1 ?? this.previousAddress?.addressLine1 ?? "",
      required: true,
    });
    this.addressLine2 = new StringFieldModel({
      value: data?.addressLine2 ?? this.previousAddress?.addressLine2 ?? "",
    });
    this.zip = new StringFieldModel({
      value: data?.zip ?? this.previousAddress?.zip ?? "",
      required: true,
    });
    this.city = new StringFieldModel({
      value: data?.city ?? this.previousAddress?.city ?? "",
      required: true,
    });
    this.country = new StringFieldModel({
      value: data?.country ?? this.previousAddress?.country ?? "",
    });
    this.mailingAddressLine1 = new StringFieldModel({
      value: data?.mailingAddressLine1 ?? this.previousMailingAddress?.addressLine1 ?? "",
      required: true,
    });
    this.mailingAddressLine2 = new StringFieldModel({
      value: data?.mailingAddressLine2 ?? this.previousMailingAddress?.addressLine2 ?? "",
    });
    this.mailingZip = new StringFieldModel({
      value: data?.mailingZip ?? this.previousMailingAddress?.zip ?? "",
      required: true,
    });
    this.mailingCity = new StringFieldModel({
      value: data?.mailingCity ?? this.previousMailingAddress?.city ?? "",
      required: true,
    });
    this.mailingCountry = new StringFieldModel({
      value: defaultMailingCountry(
        this.changeOptions,
        data?.mailingCountry ?? this.previousMailingAddress?.country,
      ),
    });
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get changesBusinessAddress(): boolean {
    return this.changeOptions.selectedIds.has(ChangeOfAddressOption.businessAddress);
  }

  get changesMailingAddress(): boolean {
    return this.changeOptions.selectedIds.has(ChangeOfAddressOption.mailingAddress);
  }

  get removesMailingAddress(): boolean {
    return this.changeOptions.selectedIds.has(ChangeOfAddressOption.removeMailingAddress);
  }

  get hasChangeArticlesOfAssociationDecision(): boolean {
    return this.context.jsonData.decisions.some(
      (decision) => decision.decisionData.type === DecisionType.changeArticlesOfAssociation,
    );
  }

  get usesNorwegianMailingAddressLookup(): boolean {
    const country = this.mailingCountry.inputValue.trim().toUpperCase();
    return !country || country === norwayCountryCode;
  }

  get businessFields(): StringFieldModel[] {
    return [this.addressLine1, this.addressLine2, this.zip, this.city, this.country];
  }

  get mailingFields(): StringFieldModel[] {
    return [
      this.mailingAddressLine1,
      this.mailingAddressLine2,
      this.mailingZip,
      this.mailingCity,
      this.mailingCountry,
    ];
  }

  get fields(): StringFieldModel[] {
    return [
      ...(this.changesBusinessAddress ? this.businessFields : []),
      ...(this.changesMailingAddress ? this.mailingFields : []),
    ];
  }

  handleChangeOptions(changedOptions: ChangeOfAddressOption[]) {
    const changedOption = changedOptions[0];
    if (changedOption === ChangeOfAddressOption.mailingAddress && this.changesMailingAddress) {
      this.changeOptions.selectedIds.delete(ChangeOfAddressOption.removeMailingAddress);
      if (this.mailingCountry.isEmpty) {
        this.selectMailingCountry(norwayCountryCode);
      }
    }
    if (
      changedOption === ChangeOfAddressOption.removeMailingAddress &&
      this.removesMailingAddress
    ) {
      this.changeOptions.selectedIds.delete(ChangeOfAddressOption.mailingAddress);
    }
    this.clearUnselectedErrors();
  }

  selectZip(item: ZipItem) {
    this.zip.setAndCommitInputValue(item.zip);
    this.city.setAndCommitInputValue(item.city);
  }

  selectMailingZip(item: ZipItem) {
    this.mailingZip.setAndCommitInputValue(item.zip);
    this.mailingCity.setAndCommitInputValue(item.city);
  }

  selectCity(city: string) {
    this.city.setAndCommitInputValue(city);
  }

  selectMailingCity(city: string) {
    this.mailingCity.setAndCommitInputValue(city);
  }

  selectCountry(code: string) {
    this.country.setAndCommitInputValue(code);
  }

  selectMailingCountry(code: string) {
    this.mailingCountry.setAndCommitInputValue(code);
  }

  addChangeArticlesOfAssociationDecision() {
    if (!this.isProjectLocked && !this.hasChangeArticlesOfAssociationDecision) {
      this.dispatch?.(
        addDecisionsEvent([{ decisionType: DecisionType.changeArticlesOfAssociation }]),
      );
    }
  }

  zipItems(filter: string): ZipItem[] {
    return zipItems(filter);
  }

  cityItems(filter: string): string[] {
    return cityItems(filter);
  }

  validate(): boolean {
    this.changeOptions.validate();
    this.clearUnselectedErrors();
    this.fields.forEach((f) => f.validate());
    return this.isValid;
  }

  get isValid(): boolean {
    return this.changeOptions.isValid && this.fields.every((f) => f.isValid);
  }

  private clearUnselectedErrors() {
    if (!this.changesBusinessAddress) {
      this.businessFields.forEach((f) => f.setError(undefined));
    }
    if (!this.changesMailingAddress) {
      this.mailingFields.forEach((f) => f.setError(undefined));
    }
  }
}

export { lookupNorwegianCity };
export type { ZipItem };
